import type { ProcessedLogo } from "./api.ts";
import { h } from "./dom.ts";

// The inline cropper over the artwork, the same on the designer, the Studio, the client page and the fix page. The box
// is kept in the logo's own pixels (its processed image, which DPI is worked out from) and shown in percentages of the
// picture, so it follows the picture's shown size; a drag is turned from screen pixels into the logo's by the
// picture's shown width. The server takes the box as whole pixels at least MIN_CROP_PX a side (src/lib/logos.ts).

export const MIN_CROP_PX = 32;
export const CROP_COPY = {
  crop: "Crop",
  apply: "Apply crop",
  cancel: "Cancel",
  reset: "Reset to the original",
  hint: "Drag the corners to trim your artwork.",
  applying: "Cropping...",
} as const;

export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type Corner = "nw" | "ne" | "sw" | "se";
export const CORNERS: Corner[] = ["nw", "ne", "sw", "se"];
const CORNER_LABEL: Record<Corner, string> = { nw: "top left", ne: "top right", sw: "bottom left", se: "bottom right" };

// One cropper while it is open: the logo's size and the box. The page keeps it in its state, so a rebuild of the page
// keeps the box where the buyer left it.
export interface CropSession {
  logoId: string;
  widthPx: number;
  heightPx: number;
  rect: CropRect;
}

// Only artwork with room for a crop of at least MIN_CROP_PX a side offers one.
export const canCrop = (logo: { widthPx: number; heightPx: number }) => logo.widthPx >= MIN_CROP_PX && logo.heightPx >= MIN_CROP_PX;

export function openCrop(logo: { logoId: string; widthPx: number; heightPx: number }): CropSession {
  return { logoId: logo.logoId, widthPx: logo.widthPx, heightPx: logo.heightPx, rect: { x: 0, y: 0, w: logo.widthPx, h: logo.heightPx } };
}

// Screen pixels to the logo's: the picture shows `shownWidth` px of a `widthPx` px logo. Zero while it has no size.
export function logoPxPerScreenPx(shownWidth: number, widthPx: number): number {
  return shownWidth > 0 && widthPx > 0 ? widthPx / shownWidth : 0;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// The box moved by (dx, dy) logo px, kept inside the logo.
export function moveRect(r: CropRect, dx: number, dy: number, widthPx: number, heightPx: number): CropRect {
  return { x: clamp(r.x + dx, 0, widthPx - r.w), y: clamp(r.y + dy, 0, heightPx - r.h), w: r.w, h: r.h };
}

// The box with one corner dragged by (dx, dy) logo px: the opposite corner stays put, the box stays inside the logo
// and at least MIN_CROP_PX a side (or the whole logo, when that is smaller).
export function dragCorner(r: CropRect, corner: Corner, dx: number, dy: number, widthPx: number, heightPx: number): CropRect {
  const minW = Math.min(MIN_CROP_PX, widthPx);
  const minH = Math.min(MIN_CROP_PX, heightPx);
  let left = r.x, top = r.y, right = r.x + r.w, bottom = r.y + r.h;
  if (corner === "nw" || corner === "sw") left = clamp(left + dx, 0, right - minW);
  else right = clamp(right + dx, left + minW, widthPx);
  if (corner === "nw" || corner === "ne") top = clamp(top + dy, 0, bottom - minH);
  else bottom = clamp(bottom + dy, top + minH, heightPx);
  return { x: left, y: top, w: right - left, h: bottom - top };
}

// What is sent: whole logo pixels, inside the logo and at least MIN_CROP_PX a side.
export function wholeRect(r: CropRect, widthPx: number, heightPx: number): CropRect {
  const w = clamp(Math.round(r.w), Math.min(MIN_CROP_PX, widthPx), widthPx);
  const hh = clamp(Math.round(r.h), Math.min(MIN_CROP_PX, heightPx), heightPx);
  return { x: clamp(Math.round(r.x), 0, widthPx - w), y: clamp(Math.round(r.y), 0, heightPx - hh), w, h: hh };
}

// The arrow keys move the box a step; with Shift they grow or shrink it from its bottom right corner. The step is
// about 2% of the logo's longer side, in logo px. Null for any other key.
export function keyRect(r: CropRect, key: string, shift: boolean, widthPx: number, heightPx: number): CropRect | null {
  const dir: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
  const d = dir[key];
  if (!d) return null;
  const step = Math.max(1, Math.round(Math.max(widthPx, heightPx) / 50));
  return shift ? dragCorner(r, "se", d[0] * step, d[1] * step, widthPx, heightPx) : moveRect(r, d[0] * step, d[1] * step, widthPx, heightPx);
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`;
// The box and the clear window in the shade over the picture share one place.
function placeBox(s: CropSession, ...els: HTMLElement[]) {
  for (const el of els) {
    el.style.left = pct(s.rect.x, s.widthPx);
    el.style.top = pct(s.rect.y, s.heightPx);
    el.style.width = pct(s.rect.w, s.widthPx);
    el.style.height = pct(s.rect.h, s.heightPx);
  }
}

// The artwork a crop leaves the page with. A cropped cutout has no background left to remove or keep, so it offers
// neither (the server still records it as a cutout).
export function croppedArtwork(c: ProcessedLogo): { original: ProcessedLogo } {
  return { original: c.backgroundRemoved ? { ...c, backgroundDetected: false } : c };
}

// A width kept where it still fits the new artwork's room, else brought inside it.
export const keepWidth = (width: number, b: { min: number; max: number }) => Math.min(b.max, Math.max(b.min, width));

type PointerTarget = Pick<HTMLElement, "addEventListener" | "setPointerCapture" | "releasePointerCapture" | "hasPointerCapture">;
// A pointer drag on one element, in screen px from where it went down (like bindDrag, which the overlays use).
function onDrag(el: PointerTarget, begin: () => boolean, move: (dx: number, dy: number) => void) {
  let drag: { id: number; x: number; y: number } | null = null;
  el.addEventListener("pointerdown", (e: PointerEvent) => {
    e.stopPropagation();
    if (drag || (e.pointerType === "mouse" && e.button !== 0) || !begin()) return;
    e.preventDefault();
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY };
    el.setPointerCapture(e.pointerId);
  });
  el.addEventListener("pointermove", (e: PointerEvent) => {
    if (drag && e.pointerId === drag.id) move(e.clientX - drag.x, e.clientY - drag.y);
  });
  const stop = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    drag = null;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };
  el.addEventListener("pointerup", stop);
  el.addEventListener("pointercancel", stop);
}

// The cropper: the artwork with the box over it, the hint, and Apply crop / Cancel. busy shows Apply as working and
// locks everything.
export function cropView(s: CropSession, opts: { src: string; busy: boolean; locked: boolean; onApply(rect: CropRect): void; onCancel(): void }): HTMLElement {
  const img = h("img", { class: "crop-img checker", src: opts.src, alt: "Your artwork, being cropped", draggable: false });
  const box = h("div", { class: "crop-box", tabindex: opts.locked ? "-1" : "0", role: "group",
    "aria-label": "Crop area. Arrow keys move it; Shift and the arrow keys resize it." });
  const clear = h("div", { class: "crop-window" });
  const place = () => placeBox(s, box, clear);
  const scale = () => logoPxPerScreenPx(img.getBoundingClientRect().width, s.widthPx);
  let start: CropRect | null = null;
  const begin = () => {
    if (opts.locked || !(scale() > 0)) return false;
    start = { ...s.rect };
    box.focus({ preventScroll: true });
    return true;
  };
  onDrag(box, begin, (dx, dy) => {
    const k = scale();
    if (!start || !(k > 0)) return;
    s.rect = moveRect(start, dx * k, dy * k, s.widthPx, s.heightPx);
    place();
  });
  for (const corner of CORNERS) {
    const handle = h("span", { class: `crop-handle ${corner}`, "aria-hidden": "true", "data-corner": corner, title: `Drag the ${CORNER_LABEL[corner]} corner` });
    onDrag(handle, begin, (dx, dy) => {
      const k = scale();
      if (!start || !(k > 0)) return;
      s.rect = dragCorner(start, corner, dx * k, dy * k, s.widthPx, s.heightPx);
      place();
    });
    box.append(handle);
  }
  box.addEventListener("keydown", (e: KeyboardEvent) => {
    if (opts.locked) return;
    const next = keyRect(s.rect, e.key, e.shiftKey, s.widthPx, s.heightPx);
    if (!next) return;
    e.preventDefault();
    s.rect = next;
    place();
  });
  place();
  return h("div", { class: "cropper" },
    h("p", { class: "muted crop-hint" }, CROP_COPY.hint),
    h("div", { class: "crop-frame" }, h("div", { class: "crop-stage" }, img, h("div", { class: "crop-shade" }, clear), box)),
    h("div", { class: "row" },
      h("button", { id: "crop-apply", type: "button", class: "chip on", disabled: opts.locked, onclick: () => opts.onApply(wholeRect(s.rect, s.widthPx, s.heightPx)) },
        opts.busy ? CROP_COPY.applying : CROP_COPY.apply),
      h("button", { id: "crop-cancel", type: "button", class: "chip", disabled: opts.locked, onclick: () => opts.onCancel() }, CROP_COPY.cancel)));
}

// The Crop chip, and Reset to the original once a crop is applied, for the row beside Remove background / Keep
// background. key tells one artwork's buttons from another's (the fix page has several).
export function cropChips(opts: { canCrop: boolean; cropped: boolean; locked: boolean; key?: string; onCrop(): void; onReset(): void }): HTMLElement[] {
  const id = (name: string) => (opts.key ? `${name}-${opts.key}` : name);
  const out: HTMLElement[] = [];
  if (opts.canCrop) out.push(h("button", { id: id("crop-open"), type: "button", class: "chip", disabled: opts.locked, onclick: () => opts.onCrop() }, CROP_COPY.crop));
  if (opts.cropped) out.push(h("button", { id: id("crop-reset"), type: "button", class: "chip", disabled: opts.locked, onclick: () => opts.onReset() }, CROP_COPY.reset));
  return out;
}
